<template>
  <base-card v-if="loaded">
    <template v-slot:header>
      <base-card-heading>Facility Projection</base-card-heading>
    </template>
    <div class="row">
      <div class="col-sm-6">
        <stat-row :stats="stats" />
      </div>
      <div class="col-sm-6">
        <form>
          <base-field addon-right-icon="calendar-alt" class="mb-3" label="Original Loan Repayment" label-class="col-form-label col-sm-6 text-right" row wrap-class="col-sm-6">
            <base-input v-model="originalLoanRepayment" datepicker disabled />
          </base-field>
          <base-field addon-right-icon="calendar-alt" class="mb-3" label="Projected Loan Repayment" label-class="col-form-label col-sm-6 text-right" row wrap-class="col-sm-6">
            <base-input v-model="projectedLoanRepayment" datepicker @input="getProjection" />
          </base-field>
          <base-field label="Show Statement For" label-class="col-form-label col-sm-6 text-right" row wrap-class="col-sm-6">
            <base-select v-model="month" :options="months" @input="getStatement" />
          </base-field>
        </form>
      </div>
    </div>
    <hr>
    <div class="d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center">
        <div class="mr-3">Filter Monthly Statement:</div>
        <div class="btn-group">
          <base-button :active="filters.Draw" secondary @click="toggleFilter('Draw')">Drawdowns</base-button>
          <base-button :active="filters.Fee" secondary @click="toggleFilter('Fee')">Fees</base-button>
          <base-button :active="filters.Interest" secondary @click="toggleFilter('Interest')">Interest Accrued</base-button>
          <base-button :active="filters.InterestPaid" secondary @click="toggleFilter('InterestPaid')">Interest Paid</base-button>
        </div>
        <base-button class="ml-3" :loading="exporting" secondary @click="exportToExcel">Export to Excel</base-button>
      </div>
      <div class="lead text-right">
        {{ $moment(month, 'M/YYYY').format('MMMM YYYY') }}
      </div>
    </div>
    <base-table class="mt-3">
      <thead>
        <tr>
          <th>Date</th>
          <th>Description</th>
          <th>Amount</th>
          <template v-for="(num, key) in project.facility.loans">
            <th :key="key">
              {{ $_.upperFirst($formatting.toOrdinal(key + 1)) }} Loan Balance
            </th>
          </template>
        </tr>
      </thead>
      <tbody>
        <template v-for="(date, day) in schedule">
          <tr v-for="(item, index) in date.items" :key="day.toString() + index.toString()">
            <td width="120">
              {{ $dates.toDayMonth(date.date) }}
              <br>
              <small class="text-muted">
                {{ $dates.toYear(date.date) }}
              </small>
            </td>
            <td>{{ item.description }}</td>
            <td :class="item.type === 'InterestPaid' ? 'text-danger' : 'text-success'">
              <small>
               {{ item.type === 'InterestPaid' ? '-' : '+' }}
              </small>
              {{ $formatting.toCurrency(item.amount.toFixed(2)) }}
            </td>
            <template v-for="(num, key) in project.facility.loans">
              <td :key="key">
                {{ $formatting.toCurrency(item.balances[key].toFixed(2)) }}
              </td>
            </template>
          </tr>
        </template>
        <template v-for="(text, key) in aggregations">
          <tr v-if="aggregated[key].gt(0.00)" :key="key">
            <td width="120">
              {{ $dates.toDayMonth(lastDayOfMonth) }}
              <br>
              <small class="text-muted">
                {{ $dates.toYear(lastDayOfMonth) }}
              </small>
            </td>
            <td>{{ text }}</td>
            <td :colspan="project.facility.loans.length + 1" :class="key === 'interestPaid' ? 'text-danger' : 'text-success'">
              <small>{{ key === 'interestPaid' ? '-' : '+' }}</small>
              {{ $formatting.toCurrency(aggregated[key].toFixed(2)) }}
            </td>
          </tr>
        </template>
      </tbody>
    </base-table>
    <hr>
    <div class="d-flex align-items-center justify-content-between py-2">
      <div class="d-flex align-items-center">
        <div>By Month Summary</div>
      </div>
    </div>
    <base-table class="mt-3">
      <thead>
        <tr>
          <th>Date</th>
          <th>Description</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(aggregate, month) in projected.monthlyAggregates">
          <template v-for="(text, key) in aggregationsAnnual">
            <tr v-if="aggregate[key].gt(0.00)" :key="month + key">
              <td width="120">
                {{ $moment(month, 'M/YYYY').endOf('month').format('Do MMM') }}
                <br>
                <small class="text-muted">
                  {{ $moment(month, 'M/YYYY').format('YYYY') }}
                </small>
              </td>
              <td>{{ text }} <span class="font-weight-bold">{{ $moment(month, 'M/YYYY').format('MMMM YYYY') }}</span></td>
              <td :class="key === 'interestPaid' ? 'text-danger' : 'text-success'">
                <small>{{ key === 'interestPaid' ? '-' : '+' }}</small>
                {{ $formatting.toCurrency(aggregate[key].toFixed(2)) }}
              </td>
            </tr>
          </template>
        </template>
      </tbody>
    </base-table>
  </base-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { Facility } from '@saxon-trust/facility-modelling'

import StatRow from './StatGrid.vue'

export default {
  created () {
    this.setDates()
    this.getProjection()

    this.loaded = true
  },

  data: () => ({
    aggregated: {},
    aggregations: {
      drawdowns: 'Total drawdowns this month',
      fees: 'Total fees this month',
      interestAccrued: 'Interest accrued this month',
      interestPaid: 'Interest paid this month',
      nonUtilAccrued: 'Non-Utilisation accrued this month'
    },
    aggregationsAnnual: {
      drawdowns: 'Total drawdowns for',
      fees: 'Total fees for',
      interestAccrued: 'Interest accrued for',
      interestPaid: 'Interest paid for',
      nonUtilAccrued: 'Non-Utilisation accrued for'
    },
    exporting: false,
    filters: {
      Draw: true,
      Fee: true,
      Interest: false,
      InterestPaid: false
    },
    loaded: false,
    month: '',
    months: [],
    originalLoanRepayment: '',
    projected: null,
    projectedLoanRepayment: '',
    schedule: {},
    stats: []
  }),

  name: 'FacilityProjection',

  components: {
    StatRow
  },

  computed: {
    lastDayOfMonth () {
      return this.schedule[this.schedule.length - 1].date
    },

    ...mapGetters({
      base: 'core/storage/base'
    }),

    ...mapState({
      exportedToExcel: store => store.core.facilities.exportedToExcel,
      project: store => store.core.projects.entity
    })
  },

  methods: {
    async exportToExcel () {
      this.exporting = true

      await this.$store.dispatch('core/facilities/exportToExcel', {
        id: this.project.facility._id
      })

      window.location = this.$document.path(this.exportedToExcel)

      this.exporting = false
    },

    getProjection () {
      const facility = this.$_.cloneDeep(this.project.facility)
      let projectedRepaymentDate = this.projectedLoanRepayment

      if (projectedRepaymentDate.length === 10) {
        projectedRepaymentDate = this.$moment(projectedRepaymentDate, 'DD/MM/YYYY').toISOString()
      }

      this.projected = new Facility(this.$_.merge(facility, {
        projectedRepaymentDate
      }))
      this.projected.calculate()

      this.months = []
      Object.keys(this.projected.monthlyAggregates).forEach(month => {
        this.months.push({
          text: this.$moment(month, 'M/YYYY').format('MMMM YYYY'),
          value: month
        })
      })

      this.month = this.$_.get(this.$_.head(this.months), 'value')

      this.getStatement()
      this.setStats()
    },

    getStatement () {
      let schedule = this.$_.cloneDeep(this.projected.getScheduleForMonth(
        this.$moment(this.month, 'M/YYYY').toDate()
      ))

      schedule = schedule.filter(date => {
        date.items = date.items.filter(item => this.$_.get(this.filters, item.type) === true)

        return date
      })

      this.aggregated = this.projected.monthlyAggregates[this.month]
      this.schedule = schedule
    },

    setDates () {
      this.originalLoanRepayment = this.project.facility.projectedRepaymentDate
      this.projectedLoanRepayment = this.project.facility.projectedRepaymentDate
    },

    setStats () {
      this.stats = [
        {
          stat: this.$formatting.toCurrency(this.projected.finalStatement.interestAccrued.toFixed(2) || 0),
          text: 'Total Interest'
        },
        {
          stat: this.$formatting.toCurrency(this.projected.getTotalRevenue().toFixed(2)),
          text: 'Total Revenue'
        }
      ]
    },

    toggleFilter (filter) {
      this.filters[filter] = !this.filters[filter]
    }
  },

  watch: {
    filters: {
      deep: true,
      handler () {
        this.getStatement()
      }
    },
    project: {
      deep: true,
      handler () {
        this.setDates()
        this.getProjection()
      }
    }
  }
}
</script>
